// src/pages/admin/RevenueManagementPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  FiDownload, FiFileText, FiSearch,
  FiCalendar, FiTrendingUp, FiUsers,
  FiX, FiRefreshCw, FiEye, FiCheckCircle,
  FiClock, FiMail, FiSend, FiTrash2
} from 'react-icons/fi';
import { FaEuroSign } from 'react-icons/fa';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { supabase } from '../../lib/supabaseClient';
import toast, { Toaster } from 'react-hot-toast'; // ✅ AJOUT
import { resoudreTauxRemuneration } from '../../utils/tauxRemuneration';
import { regularisationTotaleEra } from '../../utils/remboursementEra';

// ─── helper ──────────────────────────────────────────────────
const toFloat = (v) => {
  const n = parseFloat(String(v ?? '0').replace(/\s/g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
};
const TAUX_DEFAUT = 0.35;

// ════════════════════════════════════════════════════════════
//  AmountFilter (inchangé)
// ════════════════════════════════════════════════════════════
const AmountFilter = ({ value, onChange }) => {
  const [filterType, setFilterType] = useState('all');
  const [minAmount, setMinAmount]   = useState('');
  const [maxAmount, setMaxAmount]   = useState('');
  const [exactAmount, setExactAmount] = useState('');

  useEffect(() => {
    let filterValue = null;
    switch (filterType) {
      case 'exact':  filterValue = exactAmount ? parseFloat(exactAmount) : null; break;
      case 'range':  filterValue = { min: minAmount ? parseFloat(minAmount) : 0, max: maxAmount ? parseFloat(maxAmount) : Infinity }; break;
      case 'above':  filterValue = { min: minAmount ? parseFloat(minAmount) : 0, max: Infinity }; break;
      case 'below':  filterValue = { min: 0, max: maxAmount ? parseFloat(maxAmount) : Infinity }; break;
      default:       filterValue = null;
    }
    onChange(filterValue);
  }, [filterType, minAmount, maxAmount, exactAmount, onChange]);

  return (
    <div className="space-y-1.5">
      <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
        className="w-full text-xs px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500">
        <option value="all">Tous</option>
        <option value="exact">Exact</option>
        <option value="range">Plage</option>
        <option value="above">Supérieur à</option>
        <option value="below">Inférieur à</option>
      </select>
      {filterType === 'exact' && (
        <input type="number" value={exactAmount} onChange={(e) => setExactAmount(e.target.value)}
          placeholder="Montant exact" className="w-full text-xs px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500" />
      )}
      {(filterType === 'range' || filterType === 'above') && (
        <input type="number" value={minAmount} onChange={(e) => setMinAmount(e.target.value)}
          placeholder="Min" className="w-full text-xs px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500" />
      )}
      {(filterType === 'range' || filterType === 'below') && (
        <input type="number" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)}
          placeholder="Max" className="w-full text-xs px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500" />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
//  DateFilter (inchangé)
// ════════════════════════════════════════════════════════════
const DateFilter = ({ value, onChange }) => {
  const [filterType, setFilterType] = useState('all');
  const [startDate, setStartDate]   = useState('');
  const [endDate, setEndDate]       = useState('');
  const [exactDate, setExactDate]   = useState('');
  const [monthValue, setMonthValue] = useState(''); // 'YYYY-MM'

  const handlePresetFilter = (preset) => {
    const today = new Date();
    let start, end;
    switch (preset) {
      case 'thisMonth':    start = new Date(today.getFullYear(), today.getMonth(), 1); end = new Date(today.getFullYear(), today.getMonth() + 1, 0); break;
      case 'thisQuarter':  { const q = Math.floor(today.getMonth() / 3); start = new Date(today.getFullYear(), q * 3, 1); end = new Date(today.getFullYear(), q * 3 + 3, 0); break; }
      case 'thisYear':     start = new Date(today.getFullYear(), 0, 1); end = new Date(today.getFullYear(), 11, 31); break;
      default: return;
    }
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setFilterType('range');
  };

  useEffect(() => {
    let filterValue = null;
    switch (filterType) {
      case 'exact': filterValue = exactDate ? new Date(exactDate) : null; break;
      case 'range': filterValue = startDate && endDate ? { start: new Date(startDate), end: new Date(endDate) } : null; break;
      case 'month': {
        if (monthValue) {
          const [y, m] = monthValue.split('-').map(Number);
          if (y && m) {
            filterValue = { start: new Date(y, m - 1, 1), end: new Date(y, m, 0, 23, 59, 59) };
          }
        }
        break;
      }
      default:      filterValue = null;
    }
    onChange(filterValue);
  }, [filterType, startDate, endDate, exactDate, monthValue, onChange]);

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1">
        {[['thisMonth','Ce mois'],['thisQuarter','Trimestre'],['thisYear','Année']].map(([p, l]) => (
          <button key={p} onClick={() => handlePresetFilter(p)}
            className="px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-600 rounded hover:bg-blue-100">{l}</button>
        ))}
      </div>
      <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
        className="w-full text-xs px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500">
        <option value="all">Toutes dates</option>
        <option value="exact">Date exacte</option>
        <option value="month">Mois précis</option>
        <option value="range">Période</option>
      </select>
      {filterType === 'exact' && (
        <input type="date" value={exactDate} onChange={(e) => setExactDate(e.target.value)}
          className="w-full text-xs px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500" />
      )}
      {filterType === 'month' && (
        <input type="month" value={monthValue} onChange={(e) => setMonthValue(e.target.value)}
          className="w-full text-xs px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500" />
      )}
      {filterType === 'range' && (
        <div className="space-y-1">
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            placeholder="Début" className="w-full text-xs px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500" />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            placeholder="Fin" className="w-full text-xs px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500" />
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
//  StatCard (inchangé)
// ════════════════════════════════════════════════════════════
const StatCard = ({ title, value, description, icon: Icon, color = 'blue', trend }) => (
  <div className={`relative overflow-hidden bg-white rounded-lg shadow-sm border-l-4 border-${color}-500 hover:shadow-md transition-all duration-200`}>
    <div className="p-3">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider truncate">{title}</h3>
          <p className="mt-0.5 text-sm font-bold text-gray-900 truncate">{value}</p>
          {description && <p className="mt-0.5 text-[10px] text-gray-500 truncate">{description}</p>}
          {trend && (
            <div className={`mt-1 flex items-center text-[10px] ${trend.positive ? 'text-green-600' : 'text-red-600'}`}>
              <FiTrendingUp className={`mr-0.5 w-2.5 h-2.5 ${trend.positive ? '' : 'rotate-180'}`} />
              {trend.value}
            </div>
          )}
        </div>
        <div className={`ml-2 p-2 rounded-lg bg-${color}-100 flex-shrink-0`}>
          <Icon className={`w-4 h-4 text-${color}-600`} />
        </div>
      </div>
    </div>
    <div className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-${color}-400 to-${color}-600`} />
  </div>
);

// ════════════════════════════════════════════════════════════
//  RevenueManagementPage
// ════════════════════════════════════════════════════════════
const RevenueManagementPage = () => {
  const { session } = useOutletContext();

  const [allRevenueData, setAllRevenueData]     = useState([]);
  const [filteredData, setFilteredData]         = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [showFilters, setShowFilters]           = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl]       = useState(null);
  const [invoiceToConfirm, setInvoiceToConfirm] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [inlinePreviewInvoiceId, setInlinePreviewInvoiceId] = useState(null);
  // Bucket « factures » privé (01/11/2026) : l'aperçu lit une URL SIGNÉE (5 min) rendue par le serveur,
  // jamais url_pdf (URL publique enregistrée, qui ne répond plus).
  const [apercuFactureUrl, setApercuFactureUrl] = useState(null);
  const basculerApercuFacture = async (invoiceId) => {
    if (inlinePreviewInvoiceId === invoiceId) {
      setInlinePreviewInvoiceId(null);
      setApercuFactureUrl(null);
      return;
    }
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s) throw new Error('Utilisateur non connecté.');
      const res = await fetch(`/api/invoices/${invoiceId}/pdf-url`, { headers: { Authorization: `Bearer ${s.access_token}` } });
      const corps = await res.json().catch(() => ({}));
      if (!res.ok || !corps?.data?.url) throw new Error(corps?.error || `HTTP ${res.status}`);
      setApercuFactureUrl(corps.data.url);
      setInlinePreviewInvoiceId(invoiceId);
    } catch (err) {
      toast.error(`Facture indisponible : ${err.message}`);
    }
  };
  const [sendingInvoiceId, setSendingInvoiceId] = useState(null); // ✅ AJOUT

  const [filters, setFilters] = useState({
    client: '', clientSort: 'asc', year: '',
    remboursementFilter: null, revenuFilter: null,
    statusFilter: '', dateFilter: null,
  });

  // ── updateData ──────────────────────────────────────────
  const updateData = async (rowIndex, columnId, value) => {
    const originalAll      = [...allRevenueData];
    const originalFiltered = [...filteredData];

    const newFiltered     = [...filteredData];
    const rowToUpdate     = { ...newFiltered[rowIndex] };
    rowToUpdate[columnId] = value;
    if (columnId === 'taux') {
      rowToUpdate.revenuPotentiel = rowToUpdate.remboursement * parseFloat(value);
    }
    newFiltered[rowIndex] = rowToUpdate;
    setFilteredData(newFiltered);

    const allIdx = originalAll.findIndex((item) => item.rowKey === rowToUpdate.rowKey);
    if (allIdx !== -1) {
      const newAll = [...originalAll];
      newAll[allIdx] = rowToUpdate;
      setAllRevenueData(newAll);
    }

    const dbColumnMap = { statutPaiement: 'statut', datePaiement: 'date_paiement' };
    const dbColumn    = dbColumnMap[columnId];
    if (!dbColumn) return;

    const { invoiceId } = rowToUpdate;
    if (!invoiceId) {
      toast.error("Veuillez d'abord générer une facture avant de modifier le statut."); // ✅ toast
      setFilteredData(originalFiltered);
      setAllRevenueData(originalAll);
      return;
    }

    try {
      const { error } = await supabase.from('invoices').update({ [dbColumn]: value }).eq('id', invoiceId);
      if (error) throw error;
    } catch (err) {
      console.error('Erreur mise à jour Supabase:', err);
      toast.error('La mise à jour a échoué. Rétablissement des données.'); // ✅ toast
      setFilteredData(originalFiltered);
      setAllRevenueData(originalAll);
    }
  };

  // ── fetch ───────────────────────────────────────────────
  const fetchRevenueData = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('accounts')
        .select(`
          id, company_name, contact_full_name,
          address, postal_code, city, phone,
          contract_signed_at, cerfa_signed_at,
          tax_remuneration_rate, sans_prelevement,
          cerfa_era_summary_data (
            montant_ticfe_a_rembourser_ou_payer, montant_majoration_zni_a_regulariser,
            exercice_end_date, annee_fiscale
          ),
          invoices (
            id, statut, date_paiement, date_prelevement,
            montant_facture, url_pdf,
            exercice_annee, numero_facture,
            sent_at
          ),
          documents ( type, status, signed_at, created_at, metadata )
        `)
        // Contrats fiscaux signés seulement : leur taux prime sur la fiche (incident 07/10/2026).
        .eq('documents.type', 'tax_contract')
        .eq('documents.status', 'signed')
        .in('dossier_stage', [
          'cerfa_genere','traitement','cerfa_3310_genere',
          'dossier_pret_signature','en_attente_signature','signe',
        ])
        ; // sans_prelevement: on affiche TOUS les clients (prélèvement ou virement)

      if (error) throw error;

      const formattedData = [];

      (data || []).forEach((client) => {
        const { taux } = resoudreTauxRemuneration({
          tauxFiche: client.tax_remuneration_rate, contrats: client.documents, defaut: TAUX_DEFAUT,
        });

        // Regrouper cerfa_era_summary_data par année fiscale
        const remboursementByYear = {};
        (client.cerfa_era_summary_data || []).forEach(s => {
          const year = s.annee_fiscale ||
            (s.exercice_end_date ? new Date(s.exercice_end_date).getFullYear() : null);
          if (!year) return;
          remboursementByYear[year] = (remboursementByYear[year] || 0) +
            // K + N : la majoration ZNI séparée (03/11/2026) reste dans le remboursement facturé.
            Math.abs(regularisationTotaleEra(s));
        });

        // Indexer les factures par année
        const invoiceByYear = {};
        (client.invoices || []).forEach(inv => {
          if (inv.exercice_annee) invoiceByYear[inv.exercice_annee] = inv;
        });

        // Union des années connues (ERA + factures)
        const years = [
          ...new Set([
            ...Object.keys(remboursementByYear).map(Number),
            ...Object.keys(invoiceByYear).map(Number),
          ])
        ].sort((a, b) => b - a);

        // Si aucune année connue, on ignore ce client
        if (years.length === 0) return;

        years.forEach(year => {
          const remboursement = remboursementByYear[year] || 0;
          const invoice       = invoiceByYear[year]       || null;

          formattedData.push({
            // ✅ Clé unique composite client+année
            rowKey:        `${client.id}-${year}`,
            clientId:      client.id,
            clientName:    client.company_name || client.contact_full_name || '—',
            address:       client.address,
            postal_code:   client.postal_code,
            city:          client.city,
            phone:         client.phone,
            contract_signed_at: client.contract_signed_at,
            cerfa_signed_at:    client.cerfa_signed_at,
            sansPrelevement:    !!client.sans_prelevement,
            remboursement,
            exerciceAnnee: year,
            taux,
            revenuPotentiel:  remboursement * taux,
            invoiceId:        invoice?.id             ?? null,
            numeroFacture:    invoice?.numero_facture  ?? null,
            statutPaiement:   invoice?.statut          ?? 'Non facturé',
            datePaiement:     invoice?.date_paiement   ?? null,
            // Date prélèvement = source de vérité depuis le pipeline LCL
            // (alimentée par la page Prélèvements / lclPrelevementController).
            // Lecture seule ici, plus d'input manuel.
            datePrelevement:  invoice?.date_prelevement ?? null,
            url_pdf:          invoice?.url_pdf         ?? null,
            invoiceSentAt:    invoice?.sent_at         ?? null,
          });
        });
      });

      setAllRevenueData(formattedData);
    } catch (err) {
      console.error('[RevenueManagementPage] Erreur chargement:', err);
      toast.error('Erreur lors du chargement des données.'); // ✅ toast
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRevenueData(); }, [fetchRevenueData]);

  // ── filtrage ─────────────────────────────────────────────
  const applyFilters = useMemo(() => {
    let filtered = [...allRevenueData];
    if (filters.client)
      filtered = filtered.filter((i) => i.clientName.toLowerCase().includes(filters.client.toLowerCase()));
    if (filters.year)
      filtered = filtered.filter((i) => i.exerciceAnnee === parseInt(filters.year));
    if (filters.remboursementFilter) {
      if (typeof filters.remboursementFilter === 'number')
        filtered = filtered.filter((i) => i.remboursement === filters.remboursementFilter);
      else if (filters.remboursementFilter.min !== undefined)
        filtered = filtered.filter((i) => i.remboursement >= filters.remboursementFilter.min && i.remboursement <= filters.remboursementFilter.max);
    }
    if (filters.revenuFilter) {
      if (typeof filters.revenuFilter === 'number')
        filtered = filtered.filter((i) => i.revenuPotentiel === filters.revenuFilter);
      else if (filters.revenuFilter.min !== undefined)
        filtered = filtered.filter((i) => i.revenuPotentiel >= filters.revenuFilter.min && i.revenuPotentiel <= filters.revenuFilter.max);
    }
    if (filters.statusFilter)
      filtered = filtered.filter((i) => i.statutPaiement === filters.statusFilter);
    if (filters.dateFilter) {
      if (filters.dateFilter instanceof Date)
        filtered = filtered.filter((i) => i.datePrelevement && new Date(i.datePrelevement).toDateString() === filters.dateFilter.toDateString());
      else if (filters.dateFilter.start && filters.dateFilter.end)
        filtered = filtered.filter((i) => { if (!i.datePrelevement) return false; const d = new Date(i.datePrelevement); return d >= filters.dateFilter.start && d <= filters.dateFilter.end; });
    }
    if (filters.clientSort)
      filtered.sort((a, b) => { const c = a.clientName.localeCompare(b.clientName, 'fr'); return filters.clientSort === 'asc' ? c : -c; });
    return filtered;
  }, [allRevenueData, filters]);

  useEffect(() => { setFilteredData(applyFilters); }, [applyFilters]);

  const resetFilters = () => setFilters({ client: '', clientSort: 'asc', year: '', remboursementFilter: null, revenuFilter: null, statusFilter: '', dateFilter: null });

  // ── stats ────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!filteredData.length) return { clientsTermines: 0, totalRemboursements: 0, totalCaPrevisionnel: 0, totalFacturesEnAttente: 0, totalRevenusReels: 0, tauxPaiement: 0, paymentStatusData: [], monthlyData: [] };
    const totalRemboursements    = filteredData.reduce((s, r) => s + r.remboursement, 0);
    const totalCaPrevisionnel    = filteredData.reduce((s, r) => s + r.revenuPotentiel, 0);
    const totalFacturesEnAttente = filteredData.filter((r) => r.statutPaiement === 'ENVOYEE' || r.statutPaiement === 'EN_ATTENTE').reduce((s, r) => s + r.revenuPotentiel, 0);
    const totalRevenusReels      = filteredData.filter((r) => r.statutPaiement === 'PAYEE').reduce((s, r) => s + r.revenuPotentiel, 0);
    const clientsPayes           = filteredData.filter((r) => r.statutPaiement === 'PAYEE').length;
    const tauxPaiement           = (clientsPayes / filteredData.length) * 100;
    const paymentStatusData      = [{ name: 'Payé', value: clientsPayes }, { name: 'En attente', value: filteredData.length - clientsPayes }];
    const monthlyRevenueMap      = filteredData.filter((r) => r.statutPaiement === 'PAYEE' && r.datePaiement).reduce((acc, r) => { const d = new Date(r.datePaiement); const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2,'0')}`; acc[key] = (acc[key] || 0) + r.revenuPotentiel; return acc; }, {});
    const MONTHS   = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Aoû','Sep','Oct','Nov','Déc'];
    const today    = new Date();
    const monthlyData = Array.from({ length: 6 }, (_, i) => { const d = new Date(today.getFullYear(), today.getMonth() - (5 - i), 1); const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2,'0')}`; const yr = d.getFullYear() !== today.getFullYear() ? ` '${String(d.getFullYear()).slice(2)}` : ''; return { mois: `${MONTHS[d.getMonth()]}${yr}`, revenus: monthlyRevenueMap[key] || 0 }; });
    return { clientsTermines: filteredData.length, totalRemboursements, totalCaPrevisionnel, totalFacturesEnAttente, totalRevenusReels, tauxPaiement, paymentStatusData, monthlyData };
  }, [filteredData]);

  // ── export CSV ───────────────────────────────────────────
  const handleExport = () => {
    const headers = ['Client','Année','Montant à Rembourser','Taux Contrat','Revenu Potentiel','Statut Paiement','Date Prélèvement','Prélèvement','Email envoyé'];
    const rows = filteredData.map((r) => [
      r.clientName, r.exerciceAnnee ?? '', r.remboursement.toFixed(2), r.taux,
      r.revenuPotentiel.toFixed(2), r.statutPaiement,
      r.datePrelevement ? new Date(r.datePrelevement).toLocaleDateString('fr-FR') : 'Non programmée',
      r.sansPrelevement ? 'Virement' : 'Prélèvement',
      r.invoiceSentAt ? new Date(r.invoiceSentAt).toLocaleDateString('fr-FR') : 'Non',
    ]);
    const csv  = [headers, ...rows].map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `Gestion_Revenus_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── handleConfirmAndSave ────────────────────────────────
  const handleConfirmAndSave = async () => {
    if (!invoiceToConfirm) return;
    const toastId = toast.loading('Génération de la facture…');
    try {
      const { data: { session: s }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !s) throw new Error('Utilisateur non authentifié.');

      const response = await fetch('/api/invoices/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s.access_token}` },
        body: JSON.stringify({
          accountId:      invoiceToConfirm.clientId,   // ✅ accountId au lieu de clientId
          montantFacture: invoiceToConfirm.revenuPotentiel,
          clientName:     invoiceToConfirm.clientName,
          taux:           invoiceToConfirm.taux,
          exerciceAnnee:  invoiceToConfirm.exerciceAnnee,
          clientData:     invoiceToConfirm,
        }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || `Erreur ${response.status}`);

      // ✅ Fix "Facture null" — fallback si numero_facture absent
      const numFacture = result.invoice?.numero_facture || result.invoice?.id || 'générée';
      toast.success(`Facture N° ${numFacture} sauvegardée avec succès !`, { id: toastId });

      setAllRevenueData((prev) =>
        prev.map((row) =>
          row.rowKey === invoiceToConfirm.rowKey
            ? {
                ...row,
                invoiceId:      result.invoice.id,
                numeroFacture:  result.invoice.numero_facture,
                url_pdf:        result.invoice.url_pdf,
                statutPaiement: result.invoice.statut,
              }
            : row
        )
      );
      setShowPreviewModal(false);
      setInvoiceToConfirm(null);
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      toast.error(`Erreur : ${err.message}`, { id: toastId }); // ✅ toast
    }
  };

  // ── handleSendInvoice ✅ NOUVEAU ─────────────────────────
  const handleSendInvoice = async (row) => {
    if (!row.invoiceId) {
      toast.error('Générez d\'abord la facture avant de l\'envoyer.');
      return;
    }
    setSendingInvoiceId(row.invoiceId);
    const toastId = toast.loading(`Envoi de la facture à ${row.clientName}…`);
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s) throw new Error('Utilisateur non authentifié.');

      const res = await fetch(`/api/invoices/${row.invoiceId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s.access_token}` },
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || `Erreur ${res.status}`);

      const sentTo = result.data?.sentTo || result.sentTo;
      const sentAt = result.data?.sentAt || result.sentAt;

      toast.success(`Facture envoyée à ${sentTo} !`, { id: toastId });

      // ✅ Mise à jour locale du statut (allRevenueData + filteredData)
      const updater = (r) =>
        r.rowKey === row.rowKey
          ? { ...r, statutPaiement: 'ENVOYEE', invoiceSentAt: sentAt }
          : r;
      setAllRevenueData((prev) => prev.map(updater));
      setFilteredData((prev) => prev.map(updater));
    } catch (err) {
      console.error('Erreur envoi facture:', err);
      toast.error(`Erreur envoi : ${err.message}`, { id: toastId });
    } finally {
      setSendingInvoiceId(null);
    }
  };

  // ── handleMarkAsPaid ────────────────────────────────────
  // Marque une facture comme PAYEE (statut + date_paiement=today).
  // Toast custom react-hot-toast avec 2 boutons pour confirmer (pas de
  // window.confirm, cf règle gravée feedback_no_window_confirm.md).
  const handleMarkAsPaid = (row) => {
    if (!row.invoiceId) {
      toast.error('Génère d\'abord la facture avant de marquer payée.');
      return;
    }
    if (row.statutPaiement === 'PAYEE') return; // idempotent

    toast((t) => (
      <div className="flex items-center gap-2">
        <span className="text-xs">Marquer la facture <strong>{row.numeroFacture || row.clientName}</strong> comme payée ?</span>
        <button
          onClick={async () => {
            toast.dismiss(t.id);
            const toastId = toast.loading('Mise à jour…');
            const today = new Date().toISOString().slice(0, 10);
            try {
              const { error } = await supabase
                .from('invoices')
                .update({ statut: 'PAYEE', date_paiement: today })
                .eq('id', row.invoiceId);
              if (error) throw error;
              // Update local state
              const updater = (list) => list.map(r =>
                r.rowKey === row.rowKey
                  ? { ...r, statutPaiement: 'PAYEE', datePaiement: today }
                  : r,
              );
              setAllRevenueData(updater);
              setFilteredData(updater);
              toast.success('Facture marquée payée.', { id: toastId });
            } catch (err) {
              console.error('Erreur handleMarkAsPaid:', err);
              toast.error('Échec de la mise à jour.', { id: toastId });
            }
          }}
          className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px] hover:bg-emerald-700"
        >
          Confirmer
        </button>
        <button
          onClick={() => toast.dismiss(t.id)}
          className="px-2 py-0.5 rounded border border-gray-300 text-gray-700 text-[10px] hover:bg-gray-50"
        >
          Annuler
        </button>
      </div>
    ), { duration: 8000 });
  };

  // ── handleDeleteInvoice ─────────────────────────────────
  const handleDeleteInvoice = (row) => {
    if (!row.invoiceId) return;

    toast((t) => (
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-gray-900">
          Supprimer la facture {row.numeroFacture ? <span className="font-bold">{row.numeroFacture}</span> : ''} pour <span className="font-bold">{row.clientName}</span> ?
        </p>
        <p className="text-xs text-gray-500">Cette action est irréversible.</p>
        <div className="flex justify-end gap-2 mt-1">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              const toastId = toast.loading('Suppression de la facture…');
              try {
                const { data: { session: s } } = await supabase.auth.getSession();
                if (!s) throw new Error('Utilisateur non authentifié.');

                const res = await fetch(`/api/invoices/${row.invoiceId}`, {
                  method: 'DELETE',
                  headers: { Authorization: `Bearer ${s.access_token}` },
                });

                const result = await res.json();
                if (!res.ok) throw new Error(result.error || `Erreur ${res.status}`);

                toast.success(result.message || 'Facture supprimée.', { id: toastId });

                setAllRevenueData((prev) =>
                  prev.map((r) =>
                    r.rowKey === row.rowKey
                      ? { ...r, invoiceId: null, numeroFacture: null, url_pdf: null, statutPaiement: 'Non facturé', invoiceSentAt: null }
                      : r
                  )
                );
                if (inlinePreviewInvoiceId === row.invoiceId) setInlinePreviewInvoiceId(null);
              } catch (err) {
                console.error('Erreur suppression:', err);
                toast.error(`Erreur : ${err.message}`, { id: toastId });
              }
            }}
            className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
          >
            Supprimer
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  // ── loading ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  const fmt = (n) => n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

  // ════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gray-50 p-4 space-y-4">

      {/* ✅ Toast container */}
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />

      {/* ── Header ── */}
      <div className="bg-white shadow-sm border-b border-gray-200 rounded-lg">
        <div className="px-4 py-3 flex justify-between items-center">
          <div>
            <h1 className="text-sm font-bold text-gray-900">Gestion des Revenus</h1>
            <p className="text-[10px] text-gray-500 mt-0.5">Gérez et suivez vos revenus clients avec des outils avancés</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchRevenueData}
              className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
              <FiRefreshCw className="h-3 w-3" /> Actualiser
            </button>
            <button onClick={handleExport}
              className="flex items-center gap-1.5 px-2.5 py-1.5 border border-transparent rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors">
              <FiDownload className="h-3 w-3" /> Exporter CSV
            </button>
          </div>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard title="Clients Traités"     value={stats.clientsTermines}           description="Dossiers dans la vue actuelle"      icon={FiUsers}       color="blue"   />
        <StatCard title="Taux de Paiement"    value={`${stats.tauxPaiement.toFixed(1)}%`} description="Factures réglées"              icon={FiCalendar}    color="orange" />
        <StatCard title="Remboursements"      value={fmt(stats.totalRemboursements)}  description="Total versé aux clients"            icon={FaEuroSign}    color="green"  />
        <StatCard title="CA Prévisionnel"     value={fmt(stats.totalCaPrevisionnel)}  description="Total du chiffre d'affaires attendu" icon={FiTrendingUp} color="purple" />
        <StatCard title="Factures en Attente" value={fmt(stats.totalFacturesEnAttente)} description="Montant des factures émises"      icon={FiClock}       color="red"    />
        <StatCard title="Revenus Réels"       value={fmt(stats.totalRevenusReels)}    description="Chiffre d'affaires encaissé"        icon={FiCheckCircle} color="yellow" />
      </div>

      {/* ── Charts ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <h3 className="text-xs font-semibold text-gray-700 mb-3">Répartition des Paiements</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={stats.paymentStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                <Cell fill="#10b981" /><Cell fill="#f59e0b" />
              </Pie>
              <Tooltip formatter={(v) => `${v} client(s)`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <h3 className="text-xs font-semibold text-gray-700 mb-3">Évolution Mensuelle</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats.monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mois" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => `${v.toLocaleString('fr-FR')} €`} />
              <Bar dataKey="revenus" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Filtres ── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-700">Filtres</h3>
            <button onClick={() => setShowFilters(!showFilters)} className="text-[10px] text-blue-600 hover:underline">
              {showFilters ? 'Masquer' : 'Afficher plus'}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            <input type="text" value={filters.client}
              onChange={(e) => setFilters({ ...filters, client: e.target.value })}
              placeholder="Rechercher client..."
              className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500" />
            <select value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500">
              <option value="">Toutes années</option>
              {[2023,2024,2025,2026,2027].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={filters.statusFilter} onChange={(e) => setFilters({ ...filters, statusFilter: e.target.value })}
              className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500">
              <option value="">Tous statuts</option>
              <option value="GENEREE">Générée</option>
              <option value="ENVOYEE">Envoyée</option>
              <option value="EN_ATTENTE">En attente</option>
              <option value="PAYEE">Payée</option>
              <option value="Non facturé">Non facturé</option>
            </select>
            <select value={filters.clientSort} onChange={(e) => setFilters({ ...filters, clientSort: e.target.value })}
              className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500">
              <option value="asc">A → Z</option>
              <option value="desc">Z → A</option>
            </select>
            <button onClick={resetFilters}
              className="w-full text-xs px-2 py-1.5 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
              Réinitialiser
            </button>
            <span className="text-[10px] text-gray-500 self-center">{filteredData.length} résultat(s)</span>
          </div>
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-600 mb-1">Montant à rembourser</label>
                  <AmountFilter value={filters.remboursementFilter} onChange={(v) => setFilters({ ...filters, remboursementFilter: v })} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-600 mb-1">Revenus potentiels</label>
                  <AmountFilter value={filters.revenuFilter} onChange={(v) => setFilters({ ...filters, revenuFilter: v })} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-600 mb-1">Date de prélèvement</label>
                  <DateFilter value={filters.dateFilter} onChange={(v) => setFilters({ ...filters, dateFilter: v })} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Tableau ── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <h3 className="text-xs font-semibold text-gray-700">Tableau des Revenus</h3>
          <span className="text-[10px] text-gray-500">{filteredData.length} résultat(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* 11 colonnes : +Payé (marquage rapide payé) */}
                {['Client','Année','Montant','Taux (%)','Revenu Potentiel','N° Facture','Statut','Date Prélèvement','Prélèvement','Payé','Email','Actions'].map((h) => (
                  <th key={h} className="px-2 py-1 text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredData.map((row, index) => (
                 <React.Fragment key={row.rowKey}>
                  <tr className="hover:bg-gray-50 transition-colors">

                    {/* Client */}
                    <td className="px-2 py-1 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-bold text-[9px]">{(row.clientName || '?').charAt(0).toUpperCase()}</span>
                        </div>
                        <span className="text-[11px] font-medium text-gray-900 truncate max-w-[140px]">{row.clientName}</span>
                      </div>
                    </td>

                    {/* Année */}
                    <td className="px-2 py-1 whitespace-nowrap text-[11px] text-gray-700">{row.exerciceAnnee ?? '—'}</td>

                    {/* Remboursement */}
                    <td className="px-2 py-1 whitespace-nowrap text-[11px] font-medium text-gray-900">{fmt(row.remboursement)}</td>

                    {/* Taux */}
                    <td className="px-2 py-1 whitespace-nowrap">
                      <input type="number" value={row.taux}
                        onChange={(e) => updateData(index, 'taux', parseFloat(e.target.value) || 0)}
                        className="w-12 px-1 py-0.5 text-[11px] border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                        step="0.01" min="0" max="1" />
                    </td>

                    {/* Revenu potentiel */}
                    <td className="px-2 py-1 whitespace-nowrap text-[11px] font-semibold text-gray-900">{fmt(row.revenuPotentiel)}</td>

                    {/* N° Facture */}
                    <td className="px-2 py-1 whitespace-nowrap text-[11px] text-gray-700">
                      {row.numeroFacture || <span className="text-gray-400 italic">—</span>}
                    </td>

                    {/* Statut — ✅ badge aligné sur enum DB */}
                    <td className="px-2 py-1 whitespace-nowrap">
                      {row.statutPaiement === 'PAYEE' ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-green-100 text-green-700">Payée</span>
                      ) : row.statutPaiement === 'ENVOYEE' ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-blue-100 text-blue-700">Envoyée</span>
                      ) : row.statutPaiement === 'GENEREE' ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-purple-100 text-purple-700">Générée</span>
                      ) : row.statutPaiement === 'EN_ATTENTE' ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-yellow-100 text-yellow-700">En attente</span>
                      ) : (
                        <select value={row.statutPaiement}
                          onChange={(e) => updateData(index, 'statutPaiement', e.target.value)}
                          className="text-[9px] px-1.5 py-0.5 rounded-full border-0 font-semibold bg-gray-100 text-gray-600 focus:ring-1 focus:ring-blue-500">
                          <option value="Non facturé">Non facturé</option>
                          <option value="EN_ATTENTE">En attente</option>
                          <option value="PAYEE">Payée</option>
                        </select>
                      )}
                    </td>

                    {/* Date Prélèvement — lookup automatique depuis le pipeline LCL.
                        Plus d'input manuel : la valeur vient de invoices.date_prelevement
                        alimentée par la page Prélèvements. Affichage en gris si pas
                        encore programmée (cas "Non facturé" ou facture sans SEPA prévu). */}
                    <td className="px-2 py-1 whitespace-nowrap text-[11px] text-gray-700">
                      {row.datePrelevement ? (
                        <span className="font-medium">
                          {new Date(row.datePrelevement).toLocaleDateString('fr-FR')}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>

                    {/* Prélèvement */}
                    <td className="px-2 py-1 whitespace-nowrap">
                      {row.sansPrelevement ? (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700">Virement</span>
                      ) : (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-green-100 text-green-700">Prélèvement</span>
                      )}
                    </td>

                    {/* Payé — click sur bouton pour marquer payé (toast confirm) */}
                    <td className="px-2 py-1 whitespace-nowrap">
                      {row.statutPaiement === 'PAYEE' ? (
                        <div
                          className="inline-flex items-center gap-1 text-[9px] text-emerald-700 font-semibold"
                          title={row.datePaiement ? `Payé le ${new Date(row.datePaiement).toLocaleDateString('fr-FR')}` : 'Payé'}
                        >
                          <FiCheckCircle className="h-3 w-3 text-emerald-600" />
                          {row.datePaiement && (
                            <span className="text-gray-500">{new Date(row.datePaiement).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                          )}
                        </div>
                      ) : row.invoiceId ? (
                        <button
                          onClick={() => handleMarkAsPaid(row)}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                          title="Marquer la facture comme payée"
                        >
                          <FiCheckCircle className="h-2.5 w-2.5" />
                          Marquer payée
                        </button>
                      ) : (
                        <span className="text-[9px] text-gray-400 italic">—</span>
                      )}
                    </td>

                    {/* ✅ Email — colonne envoi */}
                    <td className="px-2 py-1 whitespace-nowrap">
                      {row.invoiceSentAt ? (
                        // Déjà envoyée → afficher la date + bouton renvoyer
                        <div className="flex items-center gap-1">
                          <FiCheckCircle className="h-2.5 w-2.5 text-green-500 flex-shrink-0" />
                          <span className="text-[9px] text-green-700 font-medium">
                            {new Date(row.invoiceSentAt).toLocaleDateString('fr-FR')}
                          </span>
                          <button
                            onClick={() => handleSendInvoice(row)}
                            disabled={sendingInvoiceId === row.invoiceId}
                            className="ml-0.5 flex items-center gap-0.5 px-1 py-0.5 text-[8px] font-medium bg-orange-50 text-orange-600 rounded hover:bg-orange-100 disabled:opacity-50 transition-colors"
                            title="Renvoyer la facture par email"
                          >
                            {sendingInvoiceId === row.invoiceId ? (
                              <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-orange-600" />
                            ) : (
                              <FiSend className="h-2 w-2" />
                            )}
                            Renvoyer
                          </button>
                        </div>
                      ) : row.invoiceId ? (
                        // Facture existante, pas encore envoyée → bouton Envoyer
                        <button
                          onClick={() => handleSendInvoice(row)}
                          disabled={sendingInvoiceId === row.invoiceId}
                          className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-medium bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 disabled:opacity-50 transition-colors"
                          title="Envoyer la facture par email"
                        >
                          {sendingInvoiceId === row.invoiceId ? (
                            <div className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-indigo-600" />
                          ) : (
                            <FiSend className="h-2.5 w-2.5" />
                          )}
                          Envoyer
                        </button>
                      ) : (
                        // Pas encore de facture
                        <span className="text-[9px] text-gray-400 italic">—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-2 py-1 whitespace-nowrap">
                      <div className="flex items-center gap-0.5">
                        {row.url_pdf ? (
                          <button
                            onClick={() => basculerApercuFacture(row.invoiceId)}
                            className={`p-0.5 rounded transition-colors ${inlinePreviewInvoiceId === row.invoiceId ? 'text-red-500 hover:text-red-700 hover:bg-red-50' : 'text-green-600 hover:text-green-800 hover:bg-green-50'}`}
                            title={inlinePreviewInvoiceId === row.invoiceId ? "Fermer l'aperçu" : 'Voir la facture PDF'}
                          >
                            <FiEye className="h-3 w-3" />
                          </button>
                        ) : (
                          <button
                            disabled={isPreviewLoading && invoiceToConfirm?.clientId === row.clientId}
                            onClick={async () => {
                              if (isPreviewLoading) return;
                              setIsPreviewLoading(true);
                              setInvoiceToConfirm(row);
                              try {
                                const { data: { session: s } } = await supabase.auth.getSession();
                                if (!s) throw new Error('Utilisateur non connecté.');
                                const res = await fetch('/api/invoices/preview', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s.access_token}` },
                                  body: JSON.stringify({ clientName: row.clientName, montantFacture: row.revenuPotentiel, revenuPotentiel: row.revenuPotentiel, taux: row.taux, clientData: row }),
                                });
                                if (!res.ok) throw new Error("Erreur serveur lors de la génération de l'aperçu.");
                                const blob = await res.blob();
                                setPreviewPdfUrl(URL.createObjectURL(blob));
                                setShowPreviewModal(true);
                              } catch (err) {
                                toast.error(err.message); // ✅ toast
                              } finally {
                                setIsPreviewLoading(false);
                              }
                            }}
                            className="p-0.5 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors"
                            title="Générer une facture"
                          >
                            {isPreviewLoading && invoiceToConfirm?.clientId === row.clientId
                              ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500" />
                              : <FiFileText className="h-3 w-3" />}
                          </button>
                        )}
                        <button
                          onClick={() => toast(`${row.clientName}\nAnnée: ${row.exerciceAnnee}\n${fmt(row.remboursement)} → ${fmt(row.revenuPotentiel)}`, { icon: '📋' })}
                          className="p-0.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
                          title="Voir les détails"
                        >
                          <FiSearch className="h-3 w-3" />
                        </button>
                        {row.statutPaiement === 'GENEREE' && row.invoiceId && (
                          <button
                            onClick={() => handleDeleteInvoice(row)}
                            className="p-0.5 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Supprimer la facture"
                          >
                            <FiTrash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* ── Aperçu inline PDF ── */}
                  {inlinePreviewInvoiceId === row.invoiceId && apercuFactureUrl && (
                    <tr>
                      <td colSpan={10} className="px-3 py-3 bg-gray-50"> {/* ✅ 10 colonnes */}
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-white px-3 py-2 border-b border-gray-200 flex justify-between items-center">
                            <h4 className="text-xs font-semibold text-gray-800">Aperçu de la facture – {row.clientName}</h4>
                            <button onClick={() => { setInlinePreviewInvoiceId(null); setApercuFactureUrl(null); }} className="text-gray-400 hover:text-gray-600 p-0.5">
                              <FiX className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <iframe key={row.invoiceId} src={apercuFactureUrl} className="w-full h-80 border-0" title={`Aperçu facture ${row.clientName} – ${row.exerciceAnnee}`} />
                          <div className="bg-white px-3 py-2 border-t border-gray-200 flex justify-end">
                            <a href={apercuFactureUrl} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">
                              <FiDownload className="h-3 w-3" /> Télécharger
                            </a>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {filteredData.length === 0 && (
          <div className="text-center py-10">
            <FiSearch className="mx-auto h-8 w-8 text-gray-300 mb-2" />
            <h3 className="text-xs font-semibold text-gray-700 mb-1">Aucun résultat trouvé</h3>
            <p className="text-[10px] text-gray-500">Essayez de modifier vos critères de recherche ou de réinitialiser les filtres.</p>
          </div>
        )}

        {filteredData.length > 0 && (
          <div className="bg-white px-3 py-2 border-t border-gray-200 flex justify-between items-center">
            <p className="text-[10px] text-gray-500">
              Affichage de <span className="font-medium">1</span> à <span className="font-medium">{filteredData.length}</span> sur <span className="font-medium">{filteredData.length}</span> résultats
            </p>
            <div className="flex gap-1">
              <button className="px-2 py-0.5 text-xs bg-white border border-gray-200 rounded disabled:opacity-50" disabled>Précédent</button>
              <span className="px-2 py-0.5 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded">1</span>
              <button className="px-2 py-0.5 text-xs bg-white border border-gray-200 rounded disabled:opacity-50" disabled>Suivant</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modal aperçu PDF ── */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl flex flex-col" style={{ maxHeight: '90vh' }}>
            <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-sm font-bold text-gray-900">Aperçu de la facture</h2>
              <button onClick={() => { setShowPreviewModal(false); setInvoiceToConfirm(null); }}
                className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100">
                <FiX className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden" style={{ minHeight: '400px' }}>
              {previewPdfUrl
                ? <iframe src={previewPdfUrl} className="w-full h-full border-0" style={{ minHeight: '400px' }} title="Aperçu PDF" />
                : <div className="flex items-center justify-center h-40"><p className="text-xs text-gray-500">Chargement de l'aperçu…</p></div>
              }
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => { setShowPreviewModal(false); setInvoiceToConfirm(null); }}
                className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                Annuler
              </button>
              <button onClick={handleConfirmAndSave}
                className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Confirmer et Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueManagementPage;
